import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getItemReportByDateRange, getItemReportHourly, getItemReportTaxwise, getItemReportTopBottomSelling, formatDateForApi } from '../../utilities/api';

export const ITEM_REPORT_TABS = {
    FULL: 0,
    HOURLY: 1,
    TAXWISE: 2,
    PERFORMANCE: 3
};

const buildPayload = ({ fromDate, toDate, branchId }) => {
    const resolvedBranchId = branchId || 'All';
    const payload = {
        from_date: fromDate ? formatDateForApi(fromDate) : '',
        to_date: toDate ? formatDateForApi(toDate) : '',
        branch_id: resolvedBranchId
    };
    if (String(resolvedBranchId).toLowerCase() === 'all') {
        const clientId = localStorage.getItem('clientid');
        if (clientId) payload.client_id = clientId;
    }
    return payload;
};

export const fetchItemFullReport = createAsyncThunk('itemReport/fetchItemFullReport', async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
    try {
        const res = await getItemReportByDateRange(buildPayload({ fromDate, toDate, branchId }));
        if (res?.error) return rejectWithValue(res.error);
        return {
            bills: res?.data || res?.bills || [],
            summary: res?.summary || {},
            status: res?.status
        };
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch item full report');
    }
});

export const fetchItemHourlyReport = createAsyncThunk('itemReport/fetchItemHourlyReport', async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
    try {
        const res = await getItemReportHourly(buildPayload({ fromDate, toDate, branchId }));
        if (res?.error) return rejectWithValue(res.error);
        return res?.data && typeof res.data === 'object' ? res.data : {};
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch item hourly report');
    }
});

export const fetchItemTaxwiseReport = createAsyncThunk('itemReport/fetchItemTaxwiseReport', async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
    try {
        const res = await getItemReportTaxwise(buildPayload({ fromDate, toDate, branchId }));
        if (res?.error) return rejectWithValue(res.error);
        return Array.isArray(res?.data) ? res.data : [];
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch item tax-wise report');
    }
});

export const fetchItemPerformanceReport = createAsyncThunk('itemReport/fetchItemPerformanceReport', async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
    try {
        const res = await getItemReportTopBottomSelling(buildPayload({ fromDate, toDate, branchId }));
        if (res?.error) return rejectWithValue(res.error);
        return {
            report_period: res?.report_period || null,
            top_selling: Array.isArray(res?.top_selling) ? res.top_selling : [],
            bottom_selling: Array.isArray(res?.bottom_selling) ? res.bottom_selling : []
        };
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch item performance report');
    }
});

const initialState = {
    selectedBranch: null,
    fromDate: null,
    toDate: null,
    activeTab: ITEM_REPORT_TABS.FULL,

    fullReport: { bills: [], summary: {} },
    hourlyReport: {},
    taxwiseReport: [],
    performanceReport: { report_period: null, top_selling: [], bottom_selling: [] },

    loading: { full: false, hourly: false, taxwise: false, performance: false },
    error: { full: null, hourly: null, taxwise: null, performance: null },
    lastQuery: { full: null, hourly: null, taxwise: null, performance: null }
};

const itemReportSlice = createSlice({
    name: 'itemReport',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.activeTab = ITEM_REPORT_TABS.FULL;
            state.fullReport = { bills: [], summary: {} };
            state.hourlyReport = {};
            state.taxwiseReport = [];
            state.performanceReport = { report_period: null, top_selling: [], bottom_selling: [] };
            state.loading = { full: false, hourly: false, taxwise: false, performance: false };
            state.error = { full: null, hourly: null, taxwise: null, performance: null };
            state.lastQuery = { full: null, hourly: null, taxwise: null, performance: null };
        },
        clearErrors: (state) => {
            state.error = { full: null, hourly: null, taxwise: null, performance: null };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchItemFullReport.pending, (state) => {
                state.loading.full = true;
                state.error.full = null;
            })
            .addCase(fetchItemFullReport.fulfilled, (state, action) => {
                state.loading.full = false;
                state.fullReport = { bills: action.payload?.bills || [], summary: action.payload?.summary || {} };
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.full = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemFullReport.rejected, (state, action) => {
                state.loading.full = false;
                state.error.full = action.payload;
                state.fullReport = { bills: [], summary: {} };
            })
            .addCase(fetchItemHourlyReport.pending, (state) => {
                state.loading.hourly = true;
                state.error.hourly = null;
            })
            .addCase(fetchItemHourlyReport.fulfilled, (state, action) => {
                state.loading.hourly = false;
                state.hourlyReport = action.payload != null && typeof action.payload === 'object' ? action.payload : {};
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.hourly = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemHourlyReport.rejected, (state, action) => {
                state.loading.hourly = false;
                state.error.hourly = action.payload;
                state.hourlyReport = {};
            })
            .addCase(fetchItemTaxwiseReport.pending, (state) => {
                state.loading.taxwise = true;
                state.error.taxwise = null;
            })
            .addCase(fetchItemTaxwiseReport.fulfilled, (state, action) => {
                state.loading.taxwise = false;
                state.taxwiseReport = Array.isArray(action.payload) ? action.payload : [];
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.taxwise = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemTaxwiseReport.rejected, (state, action) => {
                state.loading.taxwise = false;
                state.error.taxwise = action.payload;
                state.taxwiseReport = [];
            })
            .addCase(fetchItemPerformanceReport.pending, (state) => {
                state.loading.performance = true;
                state.error.performance = null;
            })
            .addCase(fetchItemPerformanceReport.fulfilled, (state, action) => {
                state.loading.performance = false;
                state.performanceReport = action.payload || { report_period: null, top_selling: [], bottom_selling: [] };
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.performance = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemPerformanceReport.rejected, (state, action) => {
                state.loading.performance = false;
                state.error.performance = action.payload;
                state.performanceReport = { report_period: null, top_selling: [], bottom_selling: [] };
            });
    }
});

export const { setFromDate, setToDate, setSelectedBranch, setActiveTab, resetFilters, clearErrors } = itemReportSlice.actions;

export const selectItemReport = (state) => state.itemReport;
export const selectActiveTab = (state) => state.itemReport.activeTab;
export const selectSelectedBranch = (state) => state.itemReport.selectedBranch;
export const selectFromDate = (state) => state.itemReport.fromDate;
export const selectToDate = (state) => state.itemReport.toDate;
export const selectFullReport = (state) => state.itemReport.fullReport;
export const selectHourlyReport = (state) => state.itemReport.hourlyReport;
export const selectTaxwiseReport = (state) => state.itemReport.taxwiseReport;
export const selectPerformanceReport = (state) => state.itemReport.performanceReport;
export const selectItemHourlyData = (state) => state.itemReport.hourlyReport;

export const selectActiveLoading = (state) => {
    const tab = state.itemReport.activeTab;
    const l = state.itemReport.loading;
    if (tab === ITEM_REPORT_TABS.FULL) return l.full;
    if (tab === ITEM_REPORT_TABS.HOURLY) return l.hourly;
    if (tab === ITEM_REPORT_TABS.TAXWISE) return l.taxwise;
    if (tab === ITEM_REPORT_TABS.PERFORMANCE) return l.performance;
    return false;
};

export const selectActiveError = (state) => {
    const tab = state.itemReport.activeTab;
    const e = state.itemReport.error;
    if (tab === ITEM_REPORT_TABS.FULL) return e.full;
    if (tab === ITEM_REPORT_TABS.HOURLY) return e.hourly;
    if (tab === ITEM_REPORT_TABS.TAXWISE) return e.taxwise;
    if (tab === ITEM_REPORT_TABS.PERFORMANCE) return e.performance;
    return null;
};

export default itemReportSlice.reducer;
